import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    first,
    mergeMap,
    Subject,
    takeUntil
} from 'rxjs';
import {
    LgvClientService,
    LgvUserService,
    NotificationService
} from '../../core/core.module';
import { LgvCompanyService } from '../../core/services/lgv-company.service';
import { LgvLoanApplicationService } from '../../core/services/lgv-loan-application.service';
import { DebtConsolidationModel } from '../../data-models/debt-consolidation.model';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvModel } from '../../data-models/longevity.model';
import { InfoPopup } from '../info-popup/info-popup.component';
import { LoanAppFormComponent } from '../loan-app-form/loan-app-form.component';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';

@Component({
    selector: 'lgv-apply-now-btn',
    templateUrl: './apply-now-btn.component.html',
    styleUrls: ['./apply-now-btn.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

/*
    Supported Usage:
    isPublicZone is required
    loanInfoModel and lenderLoanInfoModel are optional

    The goal is to build out borrowerModel, loanInfoModel, and lenderLoanInfoModel the easiest way possible.

 */
export class ApplyNowBtnComponent implements OnInit, OnDestroy {
    @Input() isPublicZone: boolean;
    @Input() isLinkMode: boolean = false;
    @Input() rawLoanModelData: LgvLoanApp.ApplicationLoanInfo;
    @Input() rawBorrowerModel: LgvLoanApp.Borrower[] = null;

    @Input() Opportunity: LgvModel.Opportunity;
    @Input() CurrentLoan: LgvModel.Loan;
    @Input() LoanScenario: LgvModel.LoanScenario;
    //@Input() DCCOpportunity: DebtConsolidationModel.CashoutOpportunity;
    @Input() ClientInfo: LgvModel.ClientInfo;
    //@Input() ClientProperty: LgvModel.Property;
    @Input() CSSClass: string = '';
    @Input() label: string = null;

    isReady$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();

    isLoading$ = new BehaviorSubject(false);
    companyInfo$ = new BehaviorSubject(null);
    agentHasApplyUrl$ = new BehaviorSubject(false);
    isLoadingAgentInfo$ = new BehaviorSubject(false);
    loanAppCompany: LgvModel.ApplicationCompany = null;

    constructor(
        public matDialog: MatDialog,
        private lgvClientSvc: LgvClientService,
        private lgvCompanySvc: LgvCompanyService,
        private notificationSvc: NotificationService,
        private lgvLoanAppSvc: LgvLoanApplicationService,
        public lgvUserSvc: LgvUserService,
        public InfoPopupDialog: MatDialog
    ) {}

    ngOnInit(): void {}

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    onApplyNow(product: LgvPricingModel.Product) {
        let description: string = null;

        if (!!this.Opportunity) {
            description = this.Opportunity.opportunityInfo.productName;
            //this.defaultFromOpportunity();
        }
        if (product) {
            description = product.keyLabel;
        }

        if (!this.isPublicZone) {
            this.lgvClientSvc
                .PostPageClick(
                    `Apply Now ${description ? '- ' + description : ''}`
                )
                .pipe(first(), takeUntil(this.onDestroy$))
                .subscribe(x => {});
        }

        // get client info if available to avoid borrower information modal
        if (!this.isPublicZone && !this.ClientInfo) {
            this.lgvClientSvc.ClientInfo$.pipe(
                first(f => !!f),
                takeUntil(this.onDestroy$)
            ).subscribe(x => {
                this.ClientInfo = x;
                //this.buildBorrowerModelFromClientInfo(this.ClientInfo);
                this.proceedWithLoanApplication();
            });
        } else {
            this.proceedWithLoanApplication();
        }
    }

    proceedWithLoanApplication() {
        combineLatest(
            this.lgvUserSvc.PricingAgentDTO$,
            this.lgvUserSvc.loanAppCompany$
        )
            .pipe(first(([pAgent, appCompany]) => !!pAgent))
            .subscribe(([pAgent, appCompany]) => {
                let haveAppUrl = !!pAgent?.agentInfo.applicationUrl;
                let isAPIApplication = LgvUserService.isApplicationCompany(
                    appCompany
                );

                //console.log(`***onApplyNow *** P-Agent:${pAgent.agentInfo.firstName + ' ' + pAgent.agentInfo.lastName}  |  haveAppUrl: ${haveAppUrl}  |  ${haveAppUrl ? pAgent?.agentInfo.applicationUrl : ''}  |  isAPIApplication: ${isAPIApplication}  |  ${isAPIApplication ? appCompany.name : ''}`);

                if (!isAPIApplication) {
                    if (!haveAppUrl) {
                        this.notificationSvc.error(
                            'Sorry.  This agent is not set up with a direct loan application'
                        );
                        return;
                    }

                    window.open(pAgent?.agentInfo.applicationUrl, '_blank');
                    return;
                }

                // API loan app

                if (!this.rawBorrowerModel && !this.ClientInfo) {
                    // Launch modal to get borrower information
                    const dialogRef = this.matDialog.open(
                        LoanAppFormComponent,
                        {
                            width: '800px',
                            panelClass: 'dialog-container',
                            data: { agentDTO: pAgent }
                        }
                    );

                    dialogRef
                        .afterClosed()
                        .pipe(first(), takeUntil(this.onDestroy$))
                        .subscribe(borrowerModalResult => {
                            if (borrowerModalResult?.action !== true) {
                                return;
                            }

                            let borrowerInfo = borrowerModalResult.borrowerInfo;
                            this.rawBorrowerModel = [
                                {
                                    firstName: borrowerInfo.firstName,
                                    lastName: borrowerInfo.lastName,
                                    contacts: {
                                        email: borrowerInfo.email,
                                        mobilePhone: borrowerInfo.phone
                                    }
                                }
                            ];

                            this.startAPILoanApp(pAgent);
                        });
                } else {
                    this.startAPILoanApp(pAgent);
                }
            });
    }

    startAPILoanApp(pAgent: LgvModel.AgentDTO) {
        let model = this.lgvLoanAppSvc.buildLoanAppModel(
            this.Opportunity,
            this.lgvClientSvc.SelectedProperty$.value,
            !!this.ClientInfo ? [this.ClientInfo] : null,
            null,
            pAgent,
            null,
            null,
            this.rawBorrowerModel,
            this.rawLoanModelData
        );

        this.isLoading$.next(true);
        this.lgvUserSvc
            .startLoanApplication(pAgent.agentInfo.id, model)
            .pipe(first(), takeUntil(this.onDestroy$))
            .subscribe(
                loanAppResult => {
                    //console.log('Result from new Loan App:');
                    //console.log(loanAppResult);
                    this.isLoading$.next(false);
                    this.openInfoPopup('LoanApplicationThankYou');
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error starting your loan application.  Please contact your agent directly.'
                    );
                    console.log('Error starting Loan Application');
                    console.log(err);
                }
            );
    }

    //defaultFromOpportunity() {

    //    //console.log('ClientProperty');
    //    //console.log(this.ClientProperty);

    //    let opp = this.Opportunity;
    //    let property = this.lgvClientSvc.SelectedProperty$.value;
    //    let marketVal: number = null;
    //    let isPurchase: boolean = !!this.LoanScenario;

    //    if (this.CurrentLoan) {
    //        marketVal = this.CurrentLoan?.loanAnalysis?.estimatedHomeValue;
    //    }
    //    else if (isPurchase) {
    //        marketVal = this.LoanScenario.purchasePrice;
    //    } else if (property) {
    //        marketVal = property?.propertyInfo?.avmestimatedValue;
    //    }

    //    this.rawLoanModelData = {
    //        loanAmount: opp.opportunityInfo.loanAmount,
    //        purpose: { name: isPurchase ? 'Purchase' : 'Refinance' },
    //        appraisalValue: marketVal,
    //        //propertyAddress: this.buildPropertyInformation(),
    //        //purchasePrice: marketVal,
    //    }

    //    //this.lenderLoanInfoModel = {
    //    //    mortgageType: null,
    //    //    amortizationType: opp.opportunityInfo.amortizationTypeName,
    //    //    interestRate: opp.opportunityInfo.matchedRate,
    //    //    loanTerm: opp.opportunityInfo.armfixedTermMonths,
    //    //    lienType: 'First'
    //    //}

    //    //this.lenderLoanInfoModel = ApplyNowBtnComponent.convertProductToLenderLoanInfoModel(this.Opportunity);
    //}

    openInfoPopup(key: string): void {
        const dialogRef = this.InfoPopupDialog.open(InfoPopup, {
            width: '500px',
            data: { mode: key }
        });
    }
}
